import * as Sentry from '@sentry/vue';
import { match } from 'ts-pattern';
import type { NetworkError } from '~~/types/NetworkError';
import { isIgnorableNetworkError } from '~~/types/NetworkError';

const IDLE_GAP_MS = 5 * 60 * 1000;

export type NetworkErrorSource = 'query' | 'mutation' | 'direct';

export type ReportNetworkErrorOptions = {
	error: NetworkError;
	source: NetworkErrorSource;
	key: string;
	/** `query.state.dataUpdatedAt`; `0` when the query never held data, `null` for mutations. */
	lastSuccessAt: number | null;
};

/** How recently the failing request's query last worked. */
type LastSuccess = 'recent' | 'stale' | 'never' | 'unknown';

const classifyLastSuccess = (lastSuccessAt: number | null): LastSuccess => {
	if (lastSuccessAt === null) {
		return 'unknown';
	}

	if (lastSuccessAt === 0) {
		return 'never';
	}

	return Date.now() - lastSuccessAt > IDLE_GAP_MS ? 'stale' : 'recent';
};

const deservesAnIssue = (lastSuccess: LastSuccess) => match(lastSuccess)
	// The API answered this query moments ago, so something really did regress.
	.with('recent', () => true)
	// It never held data at all, so a broken first load stays worth knowing about.
	.with('never', () => true)
	// The tab sat idle and wrote to sockets that died meanwhile. Expected, and it heals itself.
	.with('stale', () => false)
	// Mutations keep no success timestamp, so a waking tab and an outage look the same.
	.with('unknown', () => false)
	.exhaustive();

/*
 * Why this exists:
 *
 * A tab open since 15:00 comes back at 18:23, refetches its stale queries, and 21 requests to
 * api.clickfire.io fail within 25ms — while Supabase, on another host, answers 200 in that same
 * second. Nothing broke on the server: the connections this tab had pooled died while it sat
 * idle (routers drop idle NAT mappings after 5-30 minutes), and one dead HTTP/2 connection takes
 * all of its streams down at once. See CLICKFIRE-V2-MS.
 *
 * Filing one issue per endpoint meant a single sleeping tab produced 20 issues nobody can fix —
 * the request never reached a server, so there is no status code to look at. A real outage
 * reports the very same "Failed to fetch" though, so we can't drop them either. What tells the
 * two apart is how long ago this query last worked, and vue-query already records that.
 */
export const reportNetworkError = ({ error, source, key, lastSuccessAt }: ReportNetworkErrorOptions) => {
	// Read these before the guard below – it narrows `error` to `never`.
	const details = {
		url: error.url,
		method: error.method,
		online: error.online,
		aborted: error.aborted,
		source,
		key,
	};

	if (isIgnorableNetworkError(error)) {
		return;
	}

	Sentry.logger.warn('[NetworkError] request failed', details);

	if (!deservesAnIssue(classifyLastSuccess(lastSuccessAt))) {
		return;
	}

	// One shared fingerprint, so a wave of simultaneous failures stays a single issue.
	Sentry.withScope((scope) => {
		scope.setTag('network_online', details.online);
		scope.setTag('request_aborted', details.aborted);
		scope.setContext('Network Details', { ...details, lastSuccessAt });
		scope.setFingerprint(['network-error']);

		Sentry.captureException(error);
	});
};
